import { useEffect, useState } from 'react';

/** Current time, refreshed every `intervalMs`, for relative timestamps. */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/** Sets the document title while the component is mounted. */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    const previous = document.title;
    document.title = title ? `${title} · ComfiConf` : 'ComfiConf';
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/** A same-origin path from a `next` query parameter, or null when it is missing or points elsewhere. */
export function safeNextPath(next: string | null): string | null {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\'))
    return null;
  if (next.startsWith('/login') || next.startsWith('/register')) return null;
  return next;
}

/** Delay before a newly mounted live region receives its first content. */
export const LIVE_REGION_DELAY_MS = 100;

/**
 * False for LIVE_REGION_DELAY_MS after mount, then true. A live region that mounts together with what it
 * announces renders its content only once this is true: screen readers announce changes to a live region that
 * is already in the accessibility tree, not the content it is inserted with.
 */
export function useLiveRegionReady(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), LIVE_REGION_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
  return ready;
}
